import TransactionActions from '../../../../redux/Transaction/actions';
import CurrencyActions from '../../../../redux/Currency/actions';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useEffectSuccess } from '../../helpers/useCompare';
import { useCallback, useRef } from 'react';

const {
    changeCurrencyCode,
    changeExchangeRate,
} = TransactionActions;

const {
    readCurrencyRate,
} = CurrencyActions;

const selectorFn = state => ({
    companyCurrencyCode: state.Auth.companyCurrencyCode,
    date: state.Transaction.date,
    currencyReadExchangeRateState: state.Currency.currencyReadExchangeRateState,
    currencyReadExchangeRateResult: state.Currency.currencyReadExchangeRateResult,
});

export function useCurrency({ form, isRecurring }) {
    const dispatch = useDispatch();
    const lastReadExchangeRate = useRef(null);

    // Redux
    const {
        companyCurrencyCode,
        date,
        currencyReadExchangeRateState,
        currencyReadExchangeRateResult,
    } = useSelector(selectorFn, shallowEqual);

    const exchangeRateChanged = useCallback((value, updateForm = false) => {
        const newValue = value === 'undefined' ? lastReadExchangeRate.current : value;
        dispatch(
            // in case of 'undefined' from BukkuNumberInput,
            // we need to set the lastReadExchangeRate.current
            changeExchangeRate(newValue),
        );
        if (updateForm)
            form.setFieldsValue({ exchange_rate: newValue });

        // only store valid values
        if (value !== 'undefined')
            lastReadExchangeRate.current = value;
    }, [dispatch, form]);

    useEffectSuccess(currencyReadExchangeRateState, () => {
        exchangeRateChanged(currencyReadExchangeRateResult, true);
    });

    const readExchangeRate = useCallback((currencyCode, date) => {
        if (isRecurring) {
            if (companyCurrencyCode === currencyCode)
                exchangeRateChanged(null, true);
            return null;
        }

        // Currency Code or Date is null
        if (companyCurrencyCode === currencyCode) {
            exchangeRateChanged(1, true);
        }
        else if (!currencyCode || !date) {
            exchangeRateChanged(1, true);
        }
        // Home currency
        else {
            dispatch(
                readCurrencyRate({
                    source_currency_code: currencyCode,
                    target_currency_code: companyCurrencyCode,
                    date,
                }),
            );
        }
        return null;
    }, [companyCurrencyCode, dispatch, exchangeRateChanged, isRecurring]);

    const currencyCodeChanged = useCallback((value, updateForm = false) => {
        dispatch(
            changeCurrencyCode(value),
        );

        if (updateForm)
            form.setFieldsValue({ currency_code: value });

        // Side effects: Read exchange rate if not home currency.
        readExchangeRate(value, date);
    }, [date, dispatch, form, readExchangeRate]);

    const onExchangeRateChange = useCallback(exchangeRate => {
        exchangeRateChanged(exchangeRate, true);
    }, [exchangeRateChanged]);

    const onCurrencyCodeChange = useCallback(currencyCode => {
        currencyCodeChanged(currencyCode);
    }, [currencyCodeChanged]);

    return {
        readExchangeRate,
        onExchangeRateChange,
        onCurrencyCodeChange,
        currencyCodeChanged,
    };
}
